import { ChevronDown, ChevronUp, Eye, EyeOff, LayoutGrid, Palette, RotateCcw } from "lucide-react";
import { ACCENTS, DENSITIES, WIDGETS, usePreferences, type WidgetId } from "../lib/preferences.js";
import { cn } from "../lib/cn.js";

/** What each widget is, so the list says more than nine bare ids. */
const LABELS: Record<WidgetId, { label: string; desc: string }> = {
  welcome: { label: "Welcome Hero", desc: "Site greeting, inverter mode and alerts" },
  estFull: { label: "Estimated Full", desc: "Time to a full bank at the present rate" },
  battery: { label: "Battery Status", desc: "Radial gauge and charge telemetry" },
  totalEnergy: { label: "Energy Summary", desc: "Delivered today against the target" },
  loadBalance: { label: "Load Balance", desc: "Inverter load against its headroom" },
  mainChart: { label: "Energy history", desc: "Day-by-day generation bars" },
  earning: { label: "Grid cost", desc: "What today's import is worth" },
  weather: { label: "Weather", desc: "Conditions over the installation" },
  energyFlow: { label: "Energy Flow", desc: "Animated grid, inverter, battery and home" },
};

/**
 * The design's customisation studio, opened from the header.
 *
 * Three columns, as the design lays them out: accent, density, and the widget
 * list. What each column changes is real and persisted -- `lib/preferences.tsx`
 * writes all three to `localStorage` -- where the design's own panel drove
 * component state that a reload threw away.
 *
 * Reordering is arrows rather than the design's drag handles. A drag with no
 * keyboard equivalent is unreachable for anyone not using a mouse, and section
 * 4 requires the whole app be operable from the keyboard.
 */
export function CustomizerPanel({ onDone }: { onDone: () => void }) {
  const { widgets, resetLayout } = usePreferences();

  return (
    <div className="mx-auto mb-6 w-full max-w-[100rem] px-4 sm:px-6 lg:px-8">
      <section
        aria-label="Dashboard customisation"
        className="rounded-card border border-line bg-card p-6 shadow-2xl"
      >
        <div className="flex flex-col justify-between gap-4 border-b border-line pb-5 md:flex-row md:items-center">
          <div>
            <h2 className="text-lg font-bold text-ink">Dashboard customisation</h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Toggle widgets, change the accent, or reorder the cards. Kept in this browser only.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={resetLayout}
              className="flex items-center gap-1.5 rounded-xl border border-line px-3 py-1.5 text-xs font-medium text-ink-muted transition-colors hover:bg-line hover:text-ink"
            >
              <RotateCcw size={13} aria-hidden="true" />
              Reset defaults
            </button>
            <button
              type="button"
              onClick={onDone}
              className="rounded-xl bg-btn px-4 py-1.5 text-xs font-semibold text-btn-ink"
            >
              Done editing
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 pt-5 lg:grid-cols-3">
          <AccentPicker />
          <DensityPicker />
          <fieldset>
            <legend className="mb-3 block text-xs font-semibold text-ink">
              {`Active widgets (${String(widgets.length)}/${String(WIDGETS.length)})`}
            </legend>
            <ul className="max-h-52 space-y-1.5 overflow-y-auto pr-1">
              {WIDGETS.map((id) => (
                <li key={id}>
                  <WidgetRow id={id} />
                </li>
              ))}
            </ul>
          </fieldset>
        </div>
      </section>
    </div>
  );
}

function AccentPicker() {
  const { accent, setAccent } = usePreferences();

  return (
    <fieldset>
      <legend className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-ink">
        <Palette size={14} aria-hidden="true" /> Colour accent
      </legend>
      <div className="grid grid-cols-2 gap-2">
        {ACCENTS.map((preset) => (
          <label
            key={preset.id}
            className={cn(
              "flex cursor-pointer items-center gap-2.5 rounded-xl border p-2 text-xs font-medium transition-colors",
              accent === preset.id
                ? "border-accent bg-line text-ink"
                : "border-line text-ink-muted hover:border-line-strong",
            )}
          >
            <input
              type="radio"
              name="accent"
              value={preset.id}
              checked={accent === preset.id}
              onChange={() => {
                setAccent(preset.id);
              }}
              className="sr-only"
            />
            <span aria-hidden="true" className="flex items-center -space-x-1">
              <Swatch color={preset.primary} />
              <Swatch color={preset.secondary} />
            </span>
            <span>{preset.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function Swatch({ color }: { color: string }) {
  return (
    <span
      className="h-3.5 w-3.5 rounded-full ring-1 ring-black/20"
      style={{ backgroundColor: color }}
    />
  );
}

function DensityPicker() {
  const { density, setDensity } = usePreferences();

  return (
    <fieldset>
      <legend className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-ink">
        <LayoutGrid size={14} aria-hidden="true" /> Layout density
      </legend>
      <div className="flex items-center gap-2">
        {DENSITIES.map((mode) => (
          <label
            key={mode}
            className={cn(
              "flex-1 cursor-pointer rounded-xl border px-3 py-2 text-center text-xs font-medium capitalize transition-colors",
              density === mode
                ? "border-btn bg-btn font-semibold text-btn-ink"
                : "border-line text-ink-muted hover:bg-line hover:text-ink",
            )}
          >
            <input
              type="radio"
              name="density"
              value={mode}
              checked={density === mode}
              onChange={() => {
                setDensity(mode);
              }}
              className="sr-only"
            />
            {mode}
          </label>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-ink-muted">
        Comfortable leaves generous padding; compact tightens it for more on one screen.
      </p>
    </fieldset>
  );
}

function WidgetRow({ id }: { id: WidgetId }) {
  const { widgets, toggleWidget, moveWidget } = usePreferences();
  const shown = widgets.includes(id);
  const at = widgets.indexOf(id);
  const spec = LABELS[id];

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 rounded-xl px-2 py-1.5 text-xs font-medium transition-colors",
        shown ? "bg-line/60 text-ink" : "text-ink-muted",
      )}
    >
      <span className="min-w-0 truncate" title={spec.desc}>
        {spec.label}
      </span>
      <span className="flex shrink-0 items-center gap-0.5">
        <Nudge
          label={`Move ${spec.label} up`}
          disabled={!shown || at <= 0}
          onClick={() => {
            moveWidget(id, "up");
          }}
        >
          <ChevronUp size={14} aria-hidden="true" />
        </Nudge>
        <Nudge
          label={`Move ${spec.label} down`}
          disabled={!shown || at < 0 || at >= widgets.length - 1}
          onClick={() => {
            moveWidget(id, "down");
          }}
        >
          <ChevronDown size={14} aria-hidden="true" />
        </Nudge>
        <button
          type="button"
          onClick={() => {
            toggleWidget(id);
          }}
          className="rounded-lg p-1 transition-colors hover:bg-line"
        >
          <span className="sr-only">{shown ? `Hide ${spec.label}` : `Show ${spec.label}`}</span>
          {shown ? (
            <Eye size={14} aria-hidden="true" className="text-accent-2" />
          ) : (
            <EyeOff size={14} aria-hidden="true" />
          )}
        </button>
      </span>
    </div>
  );
}

function Nudge({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-lg p-1 transition-colors hover:bg-line disabled:opacity-30"
    >
      <span className="sr-only">{label}</span>
      {children}
    </button>
  );
}
